import type { TypedSupabaseClient } from '../client';
import {
  normalizeSessionInactivityPolicy,
  sessionInactivityPolicyUpdateSchema,
  sessionUnlockPinMutationSchema,
  sessionUnlockPinSchema,
  type SessionInactivityPolicy,
} from '@erp/domain/sessions';
import type {
  ISessionsRepository,
  SessionPlatform,
  SessionScope,
  TouchSessionInput,
  TouchSessionResult,
  UpdateSessionInactivityPolicyInput,
  UserSession,
  VerifyUnlockPinResult,
} from '../../../interfaces/src';

/**
 * Registre des appareils connectés.
 *
 * Tout passe par des RPC SECURITY DEFINER : la table expose bien un SELECT
 * RLS, mais la liste doit joindre `auth.users` (email) et `auth.sessions`
 * (session encore vivante, IP), inatteignables depuis le client anon. Les
 * écritures n'ont volontairement aucune policy — seuls les RPC écrivent.
 */

interface SessionRow {
  id: string;
  user_id: string;
  user_email: string | null;
  device_id: string;
  platform: string;
  device_label: string | null;
  os_name: string | null;
  os_version: string | null;
  app_version: string | null;
  created_at: string;
  last_seen_at: string;
  revoked_at: string | null;
  last_ip: string | null;
  is_current: boolean;
  is_active: boolean;
}

const PLATFORMS: readonly SessionPlatform[] = [
  'desktop',
  'web',
  'ios',
  'android',
];

function toPlatform(value: string): SessionPlatform {
  return (PLATFORMS as readonly string[]).includes(value)
    ? (value as SessionPlatform)
    : 'web';
}

function mapSession(row: SessionRow): UserSession {
  return {
    id: row.id,
    userId: row.user_id,
    userEmail: row.user_email,
    deviceId: row.device_id,
    platform: toPlatform(row.platform),
    deviceLabel: row.device_label,
    osName: row.os_name,
    osVersion: row.os_version,
    appVersion: row.app_version,
    createdAt: row.created_at,
    lastSeenAt: row.last_seen_at,
    revokedAt: row.revoked_at,
    // Postgres rend un inet en notation CIDR ("102.169.151.138/32") : le /32
    // d'un hôte unique n'apporte rien à l'utilisateur.
    lastIp: row.last_ip ? row.last_ip.replace(/\/(?:32|128)$/, '') : null,
    isCurrent: row.is_current,
    isActive: row.is_active,
  };
}

type RpcClient = {
  rpc: (
    name: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: unknown }>;
};

export class SupabaseSessionsRepository implements ISessionsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private get rpc(): RpcClient {
    return this.supabase as unknown as RpcClient;
  }

  async touch(input: TouchSessionInput): Promise<TouchSessionResult> {
    const deviceId = input.deviceId?.trim();
    if (!deviceId) throw new Error('device_id_required');

    const { data, error } = await this.rpc.rpc('touch_user_session', {
      p_device_id: deviceId,
      p_platform: input.platform,
      p_device_label: input.deviceLabel ?? null,
      p_os_name: input.osName ?? null,
      p_os_version: input.osVersion ?? null,
      p_app_version: input.appVersion ?? null,
    });
    if (error) throw error;

    const result = (data ?? {}) as {
      sessionId?: string | null;
      revoked?: boolean;
      registered?: boolean;
    };
    return {
      sessionId: result.sessionId ?? null,
      revoked: result.revoked === true,
      registered: result.registered === true,
    };
  }

  async list(scope: SessionScope = 'self'): Promise<UserSession[]> {
    const { data, error } = await this.rpc.rpc('list_user_sessions', {
      p_scope: scope,
    });
    if (error) throw error;
    return ((data as SessionRow[] | null) ?? []).map(mapSession);
  }

  async revoke(sessionId: string): Promise<void> {
    const { error } = await this.rpc.rpc('revoke_user_session', {
      p_id: sessionId,
    });
    if (error) throw error;
  }

  async revokeOthers(): Promise<number> {
    const { data, error } = await this.rpc.rpc('revoke_other_user_sessions');
    if (error) throw error;
    return typeof data === 'number' ? data : 0;
  }

  async getInactivityPolicy(tenantId: string): Promise<SessionInactivityPolicy> {
    const { data, error } = await this.rpc.rpc(
      'get_current_session_inactivity_policy',
      { p_tenant_id: tenantId },
    );
    if (error) throw error;
    return normalizeSessionInactivityPolicy(data);
  }

  async updateInactivityPolicy(
    tenantId: string,
    input: UpdateSessionInactivityPolicyInput,
  ): Promise<SessionInactivityPolicy> {
    const validated = sessionInactivityPolicyUpdateSchema.parse(input);
    const { data, error } = await this.rpc.rpc(
      'update_session_inactivity_policy',
      {
        p_mode: validated.mode,
        p_tenant_id: tenantId,
        p_timeout_minutes: validated.timeoutMinutes,
      },
    );
    if (error) throw error;
    return normalizeSessionInactivityPolicy(data);
  }

  async setUnlockPin(tenantId: string, pin: string | null): Promise<void> {
    const validated = sessionUnlockPinMutationSchema.parse(pin);
    const { error } = await this.rpc.rpc(
      'set_current_user_session_unlock_pin',
      {
        p_pin: validated,
        p_tenant_id: tenantId,
      },
    );
    if (error) throw error;
  }

  async verifyUnlockPin(
    tenantId: string,
    pin: string,
  ): Promise<VerifyUnlockPinResult> {
    const validated = sessionUnlockPinSchema.parse(pin);
    const { data, error } = await this.rpc.rpc(
      'verify_current_user_session_unlock_pin',
      {
        p_pin: validated,
        p_tenant_id: tenantId,
      },
    );
    if (error) throw error;

    const result = (data ?? {}) as {
      valid?: boolean;
      locked?: boolean;
      lockedUntil?: string | null;
    };
    return {
      valid: result.valid === true,
      locked: result.locked === true,
      lockedUntil:
        typeof result.lockedUntil === 'string' ? result.lockedUntil : null,
    };
  }
}
